import React, { useCallback, useEffect, useState } from "react";
import { AppointmentCardControlled } from "./card-controlled";
import { ApplicationManageData, JobDetailsModel, NheDetails, SdaInfo } from "../../utility/application-data";
import {
  BACKGROUND_CHECK_STATUS_AIR_ENDPOINT,
  BACKGROUND_CHECK_STATUS_ENDPOINT,
  BACKGROUND_CHECK_STATUS_SF_ENDPOINT,
  buildUrl,
  DRUG_TEST_STATUS_ENDPOINT,
  RIGHT_TO_WORK_STATUS_ENDPOINT,
  DRUG_TEST_STATUS_GID_ENDPOINT,
} from "../../config/urls";
import { getCandidateData } from "../../utility/candidate-data";
import { getCaasInfo } from "../../service/get-caas-info";
import { fetchWithTimeout } from "../../helpers/fetcher";
import { ContingencyResponse, isAirJob } from "../screening-results/card-uncontrolled";
import { I9Response } from "../i9-task/i9-controlled";
import { Stage } from "../../helpers/get-settings";
import {
  getTaskDefinitionsFromConfig,
  isGlobalIdEnabledByAppConfig,
  isTaskEnabledByAppConfig,
} from "../../utility/app-config-helper";
import {
  AIR_BGC_IN_SCREENING_CHECK_TASK_ID,
  BGC_CHECK_IN_SCREENING_TASK_ID,
  DT_IN_SCREENING_CHECK_TASK_ID,
  I9_TASK_ID,
  LAUNCHED_COUNTRY,
  RIGHT_TO_WORK_TASK_DEFINITION_ID,
  RIGHT_TO_WORK_TASK_TITLE,
} from "../../config/appConfigConstants";
import { isSalesforceApplication } from "../../helpers/url-params";
import { StepsSubStepsForHire } from "../../utility/salesforce-helper";
import { isEligibleToDisplayNhe, resolveSdaResponse } from "../../helpers/utils";
import { getRegionFromCookie } from "../../utility/locale-helper";
import { dispatchOnDev } from "../../utility/dev-env-helpers";

interface AppointmentCardUncontrolledProps {
  sdaAccessor: () => Promise<SdaInfo | undefined>;
  nheAccessor: () => Promise<NheDetails | undefined>;
  jcAccessor: () => Promise<JobDetailsModel | undefined>;
  salesforceAccessor: () => Promise<JobDetailsModel | undefined>;
  applicationManageAccessor: () => Promise<ApplicationManageData | undefined>;
  trackNHEDetailsClick: () => void;
  trackStartDateDetailsClick: () => void;
  trackSupportDocClick: () => void;
  cssURLWithoutScheduleId?: string;
  appointmentSelfServiceURL: string;
  applicationId: string;
  dtResponse?: ContingencyResponse;
  bgcResponse?: ContingencyResponse;
  i9Response?: I9Response;
  stage?: Stage;
}

export const AppointmentCardUncontrolled = ({
  salesforceAccessor,
  jcAccessor,
  sdaAccessor,
  nheAccessor,
  applicationManageAccessor,
  cssURLWithoutScheduleId,
  trackNHEDetailsClick,
  trackStartDateDetailsClick,
  trackSupportDocClick,
  applicationId,
  appointmentSelfServiceURL,
  stage,
}: AppointmentCardUncontrolledProps) => {
  const [sdaDetails, setSdaDetails] = useState<SdaInfo | undefined>();
  const [nheDetails, setNheDetails] = useState<NheDetails | undefined>();
  const [jcDetails, setJcDetails] = useState<JobDetailsModel | undefined>();
  const [displayNHE, setDisplayNHE] = useState<boolean>(false);
  const [displayScreening, setDisplayScreening] = useState<boolean>(false);
  const [eligibleForCSS, setEligibleForCSS] = useState<boolean>(false);
  const [eligibleForAppointmentRescheduling, setEligibleForAppointmentRescheduling] = useState<boolean>(false);
  const [scheduleIdFound, setScheduleIdFound] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [i9Response, setI9Response] = useState<I9Response>();
  const [bgcResponse, setBgcResponse] = useState<ContingencyResponse>();
  const [dtResponse, setDtResponse] = useState<ContingencyResponse>();
  const [airBgcResponse, setAirBgcResponse] = useState<ContingencyResponse>();
  const [isSalesforce, setIsSalesforce] = useState<boolean>(false);

  const requestNheAndSda = useCallback(async () => {
    setLoading(true);
    try {
      const applicationManageDetails = await applicationManageAccessor();

      let jcDetails: JobDetailsModel | undefined;

      if (!isSalesforceApplication(applicationId)) {
        jcDetails = await jcAccessor();

        const sdaDetails = await resolveSdaResponse(applicationId, jcDetails, sdaAccessor);

        const nheDetails = await nheAccessor();

        if (applicationManageDetails?.applicationState === "CONTINGENCIES_PENDING") {
          setDisplayScreening(true);
        }

        if (applicationManageDetails?.enableCSS) {
          setEligibleForCSS(true);
        }

        if (applicationManageDetails?.applicationState && !nheDetails?.isStartTimeInPast) {
          setEligibleForAppointmentRescheduling(true);
        }

        if (jcDetails?.error) {
          dispatchOnDev(() => console.error("jcDetail Error", jcDetails?.error));

          setError(true);
        } else {
          if (jcDetails?.isScheduleReleased) {
            setScheduleIdFound(false);
          }
          setJcDetails(jcDetails);
        }

        if (sdaDetails?.errorCode) {
          dispatchOnDev(() => console.error("sdaDetails Error", sdaDetails.errorCode));

          setError(true);
        } else {
          setSdaDetails(sdaDetails);

          if (nheDetails && !nheDetails.errorStatusCode) {
            setDisplayNHE(
              isEligibleToDisplayNhe({
                nheDetails,
                applicationManageDetails,
                country: getRegionFromCookie() as LAUNCHED_COUNTRY,
              })
            );
          }
          setNheDetails(nheDetails);
          setError(false);
        }
      } else {
        if (!applicationManageDetails?.applicationStepSubStep) {
          dispatchOnDev(() => console.error("No application step and sub-step."));

          setError(true);
        } else {
          const salesforceDetails = await salesforceAccessor();
          setJcDetails(salesforceDetails);
          setDisplayNHE(false);
          if (!StepsSubStepsForHire.includes(applicationManageDetails?.applicationStepSubStep)) {
            //if not ready for hire or hire confirmed, set header to display screening
            setDisplayScreening(true);
          }
          setEligibleForCSS(false);
          setEligibleForAppointmentRescheduling(false);
          setScheduleIdFound(false);
          setIsSalesforce(true);
        }
      }

      const { candidateId, sfCandidateId, candidateGlobalId } = await getCandidateData(applicationId);

      if (!candidateId || !candidateGlobalId) {
        dispatchOnDev(() => console.error("No candidate id"));

        setError(true);
      }

      const bgcURL = buildUrl(
        isSalesforceApplication(applicationId) ? BACKGROUND_CHECK_STATUS_SF_ENDPOINT : BACKGROUND_CHECK_STATUS_ENDPOINT,
        {
          candidateId: candidateId,
          applicationId,
        }
      );

      const dtURL = isGlobalIdEnabledByAppConfig()
        ? buildUrl(DRUG_TEST_STATUS_GID_ENDPOINT, {
            candidateGlobalId,
            applicationId,
            businessLine: jcDetails?.businessLine,
          })
        : buildUrl(DRUG_TEST_STATUS_ENDPOINT, {
            sfCandidateId,
            applicationId,
            businessLine: jcDetails?.businessLine,
          });

      const airBgcURL = buildUrl(BACKGROUND_CHECK_STATUS_AIR_ENDPOINT, {
        candidateId: candidateId,
        applicationId,
      });

      const taskDefinitions = getTaskDefinitionsFromConfig(RIGHT_TO_WORK_TASK_DEFINITION_ID, applicationId);

      if (isTaskEnabledByAppConfig(I9_TASK_ID, applicationId) && taskDefinitions.isIntegratedWithCaaS) {
        if (taskDefinitions.taskTitle == RIGHT_TO_WORK_TASK_TITLE) {
          setI9Response(await getCaasInfo(candidateId, RIGHT_TO_WORK_STATUS_ENDPOINT));
        } else {
          setI9Response(await getCaasInfo(candidateId));
        }
      }

      if (isTaskEnabledByAppConfig(BGC_CHECK_IN_SCREENING_TASK_ID, applicationId)) {
        setBgcResponse(await fetchWithTimeout(bgcURL));
      }

      if (isTaskEnabledByAppConfig(DT_IN_SCREENING_CHECK_TASK_ID, applicationId)) {
        setDtResponse(await fetchWithTimeout(dtURL));
      }

      if (
        isTaskEnabledByAppConfig(AIR_BGC_IN_SCREENING_CHECK_TASK_ID, applicationId) &&
        isAirJob(jcDetails?.businessLine)
      ) {
        setAirBgcResponse(await fetchWithTimeout(airBgcURL));
      }
    } catch (e) {
      dispatchOnDev(() => console.error("card-uncontrolled error", e));

      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    requestNheAndSda();
  }, []);

  return (
    <AppointmentCardControlled
      onViewNHEDetailsClick={trackNHEDetailsClick}
      onViewStartDateDetailsClick={trackStartDateDetailsClick}
      onViewListOfDocsClick={trackSupportDocClick}
      sdaDetails={sdaDetails}
      jcDetails={jcDetails}
      nheDetails={nheDetails}
      displayNHE={displayNHE}
      displayScreening={displayScreening}
      scheduleIdFound={scheduleIdFound}
      cssURLWithoutScheduleId={cssURLWithoutScheduleId}
      appointmentSelfServiceURL={appointmentSelfServiceURL}
      onRetry={requestNheAndSda}
      loading={loading}
      error={error}
      dtResponse={dtResponse}
      bgcResponse={bgcResponse}
      i9Response={i9Response}
      airBgcResponse={airBgcResponse}
      stage={stage}
      eligibleForCSS={eligibleForCSS}
      eligibleForAppointmentRescheduling={eligibleForAppointmentRescheduling}
      isSalesforce={isSalesforce}
    />
  );
};
